'use client'
import * as React from 'react'
import { useDomEvent } from 'hooks/use-dom-event'
import throttle from 'lodash.throttle'
import { tw } from 'utils/classnames'
import { RocketIcon } from 'primitives/icons/maps-and-travel'
import { sendAmplitudeEvent, AmplitudeEvents } from '@repo/analytics/amplitude/utils'

const VISIBLE_SCROLL_POSITION = 350
const HIDE_FLAMES_SCROLL_POSITION = 300
const THROTTLE_DELAY = 50

export function ScrollToTopButton() {
  const [isVisible, setVisible] = React.useState(false)
  const [withFlames, setWithFlames] = React.useState(false)

  const updateVisibility = React.useCallback(() => {
    setVisible(window.scrollY > VISIBLE_SCROLL_POSITION)

    if (window.scrollY < HIDE_FLAMES_SCROLL_POSITION) {
      setWithFlames(false)
    }
  }, [])

  const handleScroll = React.useMemo(
    () => throttle(updateVisibility, THROTTLE_DELAY),
    [updateVisibility],
  )

  const handleClick = () => {
    setWithFlames(true)
    window.scrollTo({ top: 0, behavior: 'smooth' })
    sendAmplitudeEvent(AmplitudeEvents.SCROLLED_TO_TOP, {})
  }

  React.useEffect(() => {
    updateVisibility()
  }, [handleScroll, updateVisibility])

  useDomEvent(useDomEvent.window, 'scroll', updateVisibility, { passive: true })

  return (
    <div
      onClick={handleClick}
      className={tw(
        isVisible ? 'opacity-100' : 'opacity-0',
        'fixed bottom-[176px] z-[1100] transition-opacity duration-500',
        'md:scroll-adjust-right-[44px] scroll-adjust-right-[25px]',
      )}
      id="rocket-button"
    >
      <RocketIcon className="text-gray-purple-500 h-[30px] w-[30px] -rotate-45" />
      <RocketFlame visible={withFlames} />
      <RocketFlame visible={withFlames} className="left-[22px]" />
      <RocketFlame visible={withFlames} className="left-[12px] top-[85%]" />
    </div>
  )
}

function RocketFlame({ className, visible }: { className?: string; visible: boolean }) {
  return (
    <span
      className={tw(
        visible
          ? 'border-2 border-t-[10px] opacity-100 transition-[border-width] duration-[250ms]'
          : 'border border-transparent opacity-0',
        'border-t-aubergine-500 absolute left-1 top-[90%] h-0 w-0',
        className,
      )}
    />
  )
}
